
import React,
{
    MouseEvent as ReactMouseEvent,
    RefObject,
    useEffect,
    useMemo,
} from 'react';
import './grid.css';
import { shallowEqual, useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import TokenRender from '../Token/TokenRender';
import { setMapImages, setMapSpells, setTokens } from '../../reducers/map/mapReducerActions';
import { MapImage, mapSelector, MapShape, Token } from '../../reducers/map/mapReducer';
import { dragSelector, MoveMapImageObject, MoveSpellobject, MoveTokenObject } from '../../reducers/drag/dragReducer';
import MapImageRender from '../MapImages/MapImageRender';
import SpellRender from '../Spells/SpellRender';
import useClickManager from './useClickManager';
import MeasureTool from './MeasureTool';
import Ping from './Ping';
import { createSelector } from '@reduxjs/toolkit';
import { gridSelector } from '../../reducers/gridEdit/gridReducer';
import FogRenderer from './FogRenderer';
import { updatePartialMapOnServer } from '../../utils/updatePartialMapOnServer';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import Client from '../../utils/network';
import { ObjectId } from 'bson';

export const GRID_SIZE = 25;

const selector = createSelector(
    [gridSelector, dragSelector, mapSelector],
    (grid, drag, map) => ({
        ...grid,
        dragging: drag.dragging,
        dragInfo: drag.draggedObject,
        tokens: map.map?.tokenLayer ,
        mapImages: map.map?.objectLayer,
        spells: map.map?.spellLayer,
        pings: map.pings,
        mapId: map.mapId,
        currentLayer: map.mainLayer,
    }),
);

const updateTokenPosition = (tokenId: ObjectId, xPos: number, yPos: number, mapId: string | undefined) => {
    if (!mapId) {
        return;
    }
    Client.request(`/api/map/${mapId}/update-token`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tokenId, xPos, yPos }),
    }).then(() => {
        return;
    }).catch(() => {
        // do nothing
    });
};

const Grid = ({ size, zoom, canEdit, gridRef }
:{ size?: { width: number, height: number }, zoom: number, canEdit: boolean, gridRef: RefObject<HTMLDivElement> }) => {
    const dispatch = useDispatch();


    const {
        xOffset,
        yOffset,
        xTiles,
        yTiles,
        dragging,
        dragInfo,
        tokens,
        mapId,
        mapImages,
        spells,
        pings,
    } = useSelector(selector, shallowEqual);

    const xArray = useMemo(() => {
        return new Array(xTiles - 1).fill(0) as number[];
    }, [xOffset, xTiles, size]);

    const yArray = useMemo(() => {
        return new Array(yTiles - 1).fill(0) as number[];
    }, [yOffset, yTiles, size]);

    const handleTokenDrop = (e: ReactMouseEvent<HTMLDivElement>) => {
        const info = dragInfo as MoveTokenObject | undefined;
        const { x = 0, y = 0, width  = 0, height = 0 } = gridRef.current?.getBoundingClientRect() ?? {};
        const xPosOnGrid = (e.clientX - x);
        const yPosOnGrid = (e.clientY - y);

        const gridX = Math.floor((xPosOnGrid / width) * (xArray.length + 1));
        const gridY = Math.floor((yPosOnGrid / height) * (yArray.length + 1));
        const newTokens = tokens?.filter((token) => token !== info?.details.token) ?? [];
        newTokens.push({
            ...info?.details.token as Token,
            xPos: gridX,
            yPos: gridY,
        });
        dispatch(setTokens(newTokens));
        if (canEdit) {
            updatePartialMapOnServer({ tokenLayer: newTokens }, mapId);
        } else {
            updateTokenPosition(info?.details.token._id as ObjectId, gridX, gridY, mapId);
        }

    };

    const handleMapImageDrop = (e: ReactMouseEvent<HTMLDivElement>) => {
        const info = dragInfo as MoveMapImageObject | undefined;
        const { x = 0, y = 0 } = gridRef.current?.getBoundingClientRect() ?? {};
        const xPos = (((e.clientX - x)) / zoom);
        const yPos = (((e.clientY - y)) / zoom);

        const newImages = mapImages?.filter((mapImage) => mapImage !== info?.details.image) ?? [];

        newImages.push({
            ...info?.details.image as Token,
            xPos,
            yPos,
        });


        dispatch(setMapImages(newImages));
        updatePartialMapOnServer({ objectLayer: newImages }, mapId);
    };

    const handleSpellDrop = (e: ReactMouseEvent<HTMLDivElement>) => {
        const info = dragInfo as MoveSpellobject;
        const { x = 0, y = 0 } = gridRef.current?.getBoundingClientRect() ?? {};
        const xPos = (((e.clientX - x)) / zoom) - ((info?.details.shape.width ?? 0) * 5 / 2);
        const yPos = (((e.clientY - y)) / zoom) - ((info?.details.shape.height ?? 0) * 5 / 2);
        const newSpells = spells?.filter((spell) => spell !== info.details.shape) ?? [];

        newSpells.push({
            ...info.details.shape,
            xPos,
            yPos,
        });
        dispatch(setMapSpells(newSpells));
        updatePartialMapOnServer({ spellLayer: newSpells }, mapId);
    };

    const onDrop = (e: ReactMouseEvent<HTMLDivElement>) => {
        if(dragging) {
            switch(dragInfo?.type) {
            case 'move-token':
                handleTokenDrop(e);
                break;
            case 'move-map-image':
                handleMapImageDrop(e);
                break;
            case 'move-spell':
                handleSpellDrop(e);
                break;
            default:
                break;
            }
        }
    };

    const { children, handleDrag, handleClick } = useClickManager(({ gridRef }));

    useEffect(() => {
        const newTokens = tokens?.filter(({ xPos, yPos }) => {
            return yPos < yTiles && xPos < xTiles;
        }) ?? [];
        dispatch(setTokens(newTokens));
    }, [xTiles, yTiles]);

    // Decode the access_token to extract the current user's ID
    const userId = useMemo(() => {
        const accessToken = Cookies.get('access_token');
        if (accessToken) {
            try {
                const decodedToken: { userId: string } = (jwtDecode as (val: string) => unknown)(
                    accessToken,
                ) as { userId: string };
                // eslint-disable-next-line no-console
                return decodedToken.userId;
                // currentUserId = decodedToken.sub; // Assuming `sub` contains the user ID
            } catch {
                // do nothing
                return '';
            }
        }
    }, []) as string;

    return (
        <>
            {children}
            <div
                ref={gridRef}
                id="map-grid"
                onMouseUp={onDrop}
                draggable
                onDragStartCapture={(e) => handleDrag(e, { type: 'grid' })}
                onClick={(e) => handleClick(e, { type: 'grid'})}
                className='grid-container'
                style={{ width: size?.width, height: size?.height }}
            >
                {xArray.map((val, index) => {
                    const pos = xOffset + (index + 1) * GRID_SIZE;
                    return (
                        <div
                            key={`${pos}`}
                            className='grid-row'
                            style={{ left: `${pos}px`, top: 0, height: size?.height, width: `${1 / zoom}px` }}
                        />
                    );
                })}
                {yArray.map((val, index) => {
                    const pos = yOffset + (index + 1) * GRID_SIZE;
                    return (
                        <div
                            key={`${pos}`}
                            className='grid-col'
                            style={{ top: `${pos}px`, left:0, width: size?.width, height: `${1 / zoom}px` }}
                        />
                    );
                })}
                {canEdit && (
                    <FogRenderer transparent />
                )}
                {spells?.map((spell) => {
                    if(spell !== undefined) {
                        return (
                            <SpellRender
                                key={spell._id.toString()}
                                shape={spell}
                                style={{
                                    position: 'absolute',
                                    left: `${(spell.xPos) + xOffset}px`,
                                    top: `${(spell.yPos) + yOffset}px`,
                                    width: `${spell.width * 5}px`,
                                    height: `${spell.height * 5}px`,
                                }}
                                showHidden={canEdit}
                                updateShape={(newShape: MapShape) => {
                                    const newShapeId = newShape._id;
                                    const newShapes = spells.filter((spell) => {
                                        const shapeId = spell._id;
                                        return newShapeId.toString() !== shapeId.toString();
                                    });
                                    newShapes.push(newShape);
                                    dispatch(setMapSpells(newShapes));
                                    updatePartialMapOnServer({ spellLayer: newShapes }, mapId);
                                }}
                                onDragStart={canEdit ? (e) => handleDrag(e, { type: 'spell', spell }) : undefined}
                                onClick={canEdit ? (e) => handleClick(e, { type: 'spell', spell }) : undefined}
                            />
                        );
                    }
                })}
                {mapImages?.map((mapImage) => {
                    return (
                        <MapImageRender
                            key={mapImage._id.toString()}
                            style={{
                                position: 'absolute',
                                left: `${(mapImage.xPos) + xOffset}px`,
                                top: `${(mapImage.yPos) + yOffset}px`,
                                width: `${mapImage.width}px`,
                                height: `${mapImage.height}px`,
                            }}
                            showHidden={canEdit}
                            image={mapImage}
                            onDragStart={canEdit
                                ? (e) => handleDrag(
                                    e,
                                    { type: 'image', image: mapImage },
                                )
                                : undefined}
                            onClick={
                                canEdit
                                    ? (e) => handleClick(e, { type: 'image', image: mapImage })
                                    : undefined
                            }
                            updateImage={(newImage: MapImage) => {
                                const newImageId = newImage._id;
                                const newImages = mapImages.filter((image) => {
                                    const imageId = image._id;
                                    return newImageId.toString() !== imageId.toString();
                                });
                                newImages.push(newImage);
                                dispatch(setMapImages(newImages));
                                updatePartialMapOnServer({ objectLayer: newImages }, mapId);
                            }}
                        />
                    );
                })}
                {tokens?.map((el) => {
                    if(el !== undefined) {
                        return (
                            <TokenRender
                                key={el._id.toString()}
                                token={el}
                                style={{
                                    position: 'absolute',
                                    left: `${(GRID_SIZE * el.xPos) + xOffset}px`,
                                    top: `${(GRID_SIZE * el.yPos) + yOffset}px`,
                                    width: `${GRID_SIZE * el.width}px`,
                                    height: `${GRID_SIZE * el.height}px`,
                                }}
                                showHidden={canEdit}
                                onDragStart={
                                    (canEdit || (el.assignedUsers ?? []).includes(userId))
                                        ? (e) => handleDrag(e, {type: 'token', token: el })
                                        : undefined
                                }
                                updateToken={(newToken: Token) => {
                                    const newTokenId = newToken._id;
                                    const newTokens = tokens.filter((token) => {
                                        const tokenId = token._id;
                                        return newTokenId.toString() !== tokenId.toString();
                                    });
                                    newTokens.push(newToken);
                                    dispatch(setTokens(newTokens));
                                    updatePartialMapOnServer({ tokenLayer: newTokens }, mapId);
                                }}
                                onClick={
                                    canEdit
                                        ? (e) => handleClick(e, { type: 'token', token: el })
                                        : undefined
                                }
                            />
                        );
                    }
                    return null;
                })}
                {!canEdit && (
                    <FogRenderer />
                )}
                {
                    pings.map((ping) => (
                        <Ping
                            key={ping._id.toString()}
                            style={{
                                position: 'absolute',
                                left: `${(ping.x) + xOffset}px`,
                                top: `${(ping.y) + yOffset}px`,
                                transform: 'translate(-50%, -50%)',
                            }}
                            ping={ping}
                        />
                    ))
                }
                <MeasureTool gridRef={gridRef} />
            </div>
        </>
    );
};


export default Grid;
